import { FC } from 'react';
import cx from 'classnames';
import InfoButton from '../InfoButton/InfoButton';
import { IInfoDrawerValues } from '../../constants/infoDrawerValues';
import styles from './KpiCard.module.scss';

export type KpiTone = 'neutral' | 'warning' | 'danger';

export type KpiDeltaTone = 'neutral' | 'warn' | 'alert';

export interface IKpiDelta {
  /** Variazione % col periodo di confronto (segno = direzione). */
  pct: number;
  /** Tono già classificato dal chiamante (dominio fuori dal componente shared). */
  tone: KpiDeltaTone;
  /** Testo di contesto nel tooltip/aria (es. "vs settimana precedente"). */
  hint?: string;
}

export interface IKpiCardProps {
  label: string;
  value: string;
  sub?: string;
  tone?: KpiTone;
  /** Chip di variazione col periodo precedente. Assente = non mostrata. */
  delta?: IKpiDelta;
  /** Se presente, mostra un «?» accanto alla label che apre la guida di dominio. */
  infoKey?: keyof IInfoDrawerValues;
  /**
   * Allarme "forte": tinge l'INTERA card (sfondo + bordo error), non solo il
   * valore. Per i casi in cui il segnale deve saltare all'occhio in mezzo ad
   * altre card (es. rapporto Continuo/Intermittente sotto soglia). Distinto da
   * `tone`, che colora solo il valore.
   */
  alert?: boolean;
}

/**
 * Card KPI generica: etichetta + valore + sotto-testo opzionale + tono.
 *
 * Promossa da `AthleteEffortDetail.tsx` (tab Sforzo) — terza card KPI della
 * piattaforma dopo `StatsKpiRow` (TrainingStatisticsDashboard) e l'header di
 * `WeekBoard`: l'Analisi Allenamenti la riusa invece di introdurne una quarta
 * (design M2/M3 §1.4).
 */
const KpiCard: FC<IKpiCardProps> = ({ label, value, sub, tone = 'neutral', delta, infoKey, alert }) => (
  <div className={cx(styles.card, { [styles.cardAlert]: alert })}>
    <div className={styles.labelRow}>
      <p className={styles.label}>{label}</p>
      {infoKey && <InfoButton infoKey={infoKey} label={`Cosa significa: ${label}`} />}
    </div>
    <div className={styles.valueRow}>
      <p
        className={cx(styles.value, {
          [styles.toneWarning]: tone === 'warning',
          [styles.toneDanger]: tone === 'danger',
        })}
      >
        {value}
      </p>
      {delta && (
        <span
          className={cx(styles.delta, {
            [styles.deltaWarn]: delta.tone === 'warn',
            [styles.deltaAlert]: delta.tone === 'alert',
          })}
          title={delta.hint}
        >
          {delta.pct > 0 ? '▲' : delta.pct < 0 ? '▼' : '•'}{' '}
          {delta.pct > 0 ? '+' : ''}
          {delta.pct}%
        </span>
      )}
    </div>
    {sub && <p className={styles.sub}>{sub}</p>}
  </div>
);

export default KpiCard;
